import { Button, type ButtonProps, Flex, Switch, Text, useToast, VStack } from '@chakra-ui/react'
import { faEdit } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import React, { type ReactElement, useCallback } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import parseNonQueryNumber from '@/utils/parseNonQueryNumber'

import SeriesQ from '@/queries/SeriesQ'
import UserQ from '@/queries/UserQ'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import type { Query } from '@/client/query'
import RPC from '@/client/rpc'
import * as url from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'

import SATBootcampRescheduleAlert from '@/components/SATBootcamp/SATBootcampRescheduleAlert'
import DeleteSeriesModal from '@/components/Series/DeleteSeriesModal'
import ShwLink from '@/components/ShwLink'
import Form from '@/components/UI/Form/Form'
import type { FormBlock, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

export default function EditSeriesButton({
  series,
  isSATSeries,
  isUserHost,
  ...props
}: {
  series: Query
  isSATSeries: boolean
  isUserHost: boolean
} & ButtonProps): ReactElement {
  const router = useRouter()
  const user = useUser()
  const toast = useToast()

  const enrollmentToggled = React.useRef(false)
  const [showEditModal, setShowEditModal] = React.useState(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const [showTransferOwnershipModal, setShowTransferOwnershipModal] = React.useState(false)
  const [enrollmentClosed, setEnrollmentClosed] = React.useState(
    series(SeriesQ.isEnrollmentClosed()) ?? true
  )

  const seriesId = series(SeriesQ.id) ?? 0
  const seriesTitle = series(SeriesQ.title) ?? ''
  const seriesUserId = series(SeriesQ.user_id) ?? 0

  const isSuperAdmin = hasUserRole(user, 'admin:super')
  const isSeriesClosed = series(SeriesQ.isClosed()) ?? false
  const isUpdatingDisabled = series(SeriesQ.isUpdatingDisabled()) ?? false

  const countReschedules = series(SeriesQ.countSessionReschedulesAfterFirstRegistration()) ?? 0
  const seriesHosts = series.many(SeriesQ.getHosts({ approved: true, role: 'alternative' }))

  const transferOwnershipOptions = seriesHosts.map(seriesHost => ({
    display_name: seriesHost(UserQ.display_name) ?? '',
    id: seriesHost(UserQ.id) ?? 0,
  }))

  const transferOwnershipFormInputs = React.useMemo<FormBlock[]>(
    () => [
      {
        name: 'newHostUserId',
        type: 'select',
        label: 'New Host',
        options: transferOwnershipOptions.map(item => ({
          label: item.display_name,
          value: `${item.id}`,
        })),
        size: 6,
        required: true,
      },
    ],
    [transferOwnershipOptions]
  )

  const hideSeriesEditModal = React.useCallback(() => {
    if (enrollmentToggled.current) {
      router.reload()
    }
    setShowEditModal(false)
  }, [router])

  const hideSeriesDeleteModal = React.useCallback(() => {
    if (enrollmentToggled.current) {
      router.reload()
    }
    setShowDeleteModal(false)
  }, [router])

  const [handleEnrollmentChange] = useAPIWrapper(
    async () => {
      // Optimistic change:
      setEnrollmentClosed(!enrollmentClosed)
      try {
        await RPC.toggleSeriesEnrollment({
          seriesId,
        })
        enrollmentToggled.current = true
        toast({
          title: `Successfully ${
            enrollmentClosed ? 'opened' : 'closed'
          } enrollment for series: ${seriesTitle}`,
          status: 'success',
        })
      } catch (err) {
        // Revert back to old value
        setEnrollmentClosed(enrollmentClosed)

        throw err
      }
    },
    [enrollmentClosed, seriesId, toast, seriesTitle],
    'Enrollment Change'
  )

  const handleOpenSeriesTransferModal = useCallback(() => {
    setShowEditModal(false)
    setShowTransferOwnershipModal(true)
  }, [])

  const handleSubmitOwnership = React.useCallback(
    async (values: FormValues) => {
      const newHostUserId = parseNonQueryNumber({
        value: `${values.newHostUserId}`,
        defaultValue: 0,
      })

      await RPC.transferSeriesOwnership({
        seriesId,
        newHostUserId,
      })
      setShowTransferOwnershipModal(false)
      toast({
        title: 'Successfully transferred ownership of series',
        status: 'success',
      })
      router.reload()
    },
    [seriesId, toast, router]
  )

  return (
    <>
      <ModalV3
        size="2xl"
        title={`Edit ${seriesTitle}`}
        open={showEditModal}
        onClose={hideSeriesEditModal}
        body={() => (
          <VStack spacing="24px" align="flex-start">
            {isSATSeries && !hasUserAuthScope(user, 'admin:series_or_session:update') ? (
              <SATBootcampRescheduleAlert countReschedules={countReschedules} />
            ) : null}
            {isUserHost || hasUserAuthScope(user, 'admin:series_or_session:update') ? (
              <VStack spacing={1} align="flex-start">
                <Text as="span" fontWeight="bold">
                  Make Changes
                </Text>
                <Text as="span" mb="20px">
                  Add and delete sessions, edit descriptions, and change series settings.
                </Text>
                <ShwLink href={url.getSeriesUrl(seriesId, { path: 'update' })}>
                  <Button variant="outline" leftIcon={<FontAwesomeIcon icon={faEdit} />}>
                    Edit Series
                  </Button>
                </ShwLink>
              </VStack>
            ) : null}
            {isUserHost || isSuperAdmin ? (
              <VStack spacing={1} align="flex-start">
                <Flex direction="row" width="100%">
                  <Text as="span" fontWeight="bold">
                    Toggle Enrollment
                  </Text>
                  <Flex justifyContent="flex-end" flex={1}>
                    <Switch isChecked={!enrollmentClosed} onChange={handleEnrollmentChange} />
                  </Flex>
                </Flex>
                <Text as="span" paddingRight={2}>
                  Enrollment for your series is currently{' '}
                  <b>{enrollmentClosed ? 'closed' : 'open'}</b>. Toggle to{' '}
                  {enrollmentClosed ? 'open' : 'closed'} enrollment to new learners.
                </Text>
              </VStack>
            ) : null}
            {!isSeriesClosed && (isSuperAdmin || seriesUserId === user.id) ? (
              <VStack spacing={1} align="flex-start">
                <Text as="span" fontWeight="bold">
                  Transfer Ownership
                </Text>
                <Text as="span">Need to transfer ownership of this series?</Text>
                <Text as="span" mb="20px">
                  {isSuperAdmin
                    ? ' You can transfer it to another tutor in the series.'
                    : ' You can transfer it to another co-host.'}
                </Text>
                <Button variant="outline" onClick={handleOpenSeriesTransferModal}>
                  Transfer Ownership
                </Button>
              </VStack>
            ) : null}

            {isSeriesClosed || !isUserHost ? null : (
              <VStack spacing={1} align="flex-start">
                <Text as="span" fontWeight="bold">
                  End Series Early
                </Text>
                <Text as="span" mb={5}>
                  Need to cancel the remainder of your series? You can end it early.
                </Text>
                <Button
                  variant="outline"
                  color="fg.error"
                  onClick={() => {
                    setShowEditModal(false)
                    setShowDeleteModal(true)
                  }}
                >
                  End Series Early
                </Button>
              </VStack>
            )}
          </VStack>
        )}
      />
      <ModalV3
        size="2xl"
        title="Transfer Series Ownership"
        open={showTransferOwnershipModal}
        onClose={() => setShowTransferOwnershipModal(false)}
        body={() => (
          <>
            <Text as="span">
              Please select the new host to transfer the ownership of this series to
            </Text>
            <Form
              direction="vertical"
              name="TransferSeriesOwnership"
              onSubmit={handleSubmitOwnership}
              blocks={transferOwnershipFormInputs}
            />
          </>
        )}
      />
      <DeleteSeriesModal
        show={showDeleteModal}
        onClose={hideSeriesDeleteModal}
        seriesId={seriesId}
        seriesTitle={seriesTitle}
      />
      <Tippy disabled={!isUpdatingDisabled} content="Updates to this series are disabled.">
        <Button
          variant="outline"
          color="fg.muted"
          isDisabled={isUpdatingDisabled}
          onClick={() => setShowEditModal(true)}
          aria-label="Edit this series"
          leftIcon={
            <FontAwesomeIcon icon={faEdit} color={chakraTokenToVar('fg.muted')} size="xs" />
          }
          {...props}
        >
          Edit Series
        </Button>
      </Tippy>
    </>
  )
}
